import React, { useLayoutEffect, useImperativeHandle, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/moment';

import { useI18n } from 'i18n';
import * as widgetsActions from 'core/redux/app/widgets';
import { pagesSelector } from 'core/redux/selectors/pages';
import NoDataView from 'core/react/general/view/NoDataView';
import { widgetsSelector } from 'core/redux/selectors/widgets';
import ActionLock from 'core/react/share/actionbar/ActionLock';
import { devicesStateSelector } from 'core/redux/selectors/devices';
import WidgetMenuItem from 'core/react/general/widgets/WidgetMenuItem';
import WidgetErrorContent from 'core/react/general/widgets/WidgetErrorContent';
import DeviceShortInfoView from 'core/react/share/devices/DeviceShortInfoView';
import { useFlightInfoWidget } from 'hooks/dashboard/widgets/useFlightInfoWidget';

import '../deviceInfoWidget.scss';
import styles from './styles.module.css';

interface IFlightInfoWidgetProps {
  uid: string;
  reduxKey: string;
  expanded: boolean;
  customRef: React.RefObject<any>;
  update(): void;
}

const PIN_ACTIONS = { maximize: 'maximize', lock: 'lockDevices' };

export const FlightInfoWidget: FC<IFlightInfoWidgetProps> = (props) => {
  const { f, fc } = useI18n();
  const dispatch = useDispatch();

  const pages = useSelector(pagesSelector);
  const widgets = useSelector(widgetsSelector);
  const deviceMap = useSelector(devicesStateSelector).map;

  const widget = widgets[props.reduxKey].map[props.uid];
  const selection = pages[props.reduxKey].selection;

  const widgetData = widget && widget.data;
  const locked = !!(widgetData && widgetData.isLocked);
  const uris = locked ? widgetData.uris : selection ? selection : [];

  const uri = uris[0];
  const device = deviceMap?.[uri];

  const flightInfo = useFlightInfoWidget(device?.uri, device?.categoryIds);

  const lockHandler = () => {
    dispatch(
      widgetsActions.actions.update({
        domain: props.reduxKey,
        uid: props.uid,
        data: { isLocked: !locked, uris },
      }),
    );
  };

  useLayoutEffect(() => {
    props.update();
  }, [locked, uris?.length, widgetData]);

  useImperativeHandle(props.customRef, () => ({
    title: () => fc('Flight info'),
    actions: () => {
      const actions = [];
      if (locked) {
        actions.push(
          <ActionLock
            key={PIN_ACTIONS.lock}
            disabled={!uris.length}
            onClick={lockHandler}
            active={locked}
            title={fc(locked ? 'unlock' : 'lock')}
          />,
        );
      }
      return actions;
    },
    menuItems: () => [
      <WidgetMenuItem
        disabled={!uri}
        onClick={lockHandler}
        text={fc(locked ? 'unlock' : 'lock')}
        key={PIN_ACTIONS.lock}
      />,
    ],
    expandable: true,
  }));

  if (!uris.length) {
    return (
      <div className="device-info widget">
        <WidgetErrorContent message={f('please select aircraft')} />
      </div>
    );
  }

  if (!deviceMap || !device || !flightInfo.flight) {
    return (
      <div className="device-info widget">
        <NoDataView message={f('no data for', { name: deviceMap?.[uri]?.name || uri })} />
      </div>
    );
  }

  const { routeLeg, parkingPosition, scheduledAt } = flightInfo.flight;
  const { flightNumber, airline } = routeLeg;

  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY, HH:mm:ss');

  const fields = [
    { label: 'Flight number', value: flightNumber },
    { label: 'Airline', value: airline },
    { label: 'Parking position', value: parkingPosition || '-' },
    { label: 'Scheduled at', value: formatDate(scheduledAt) },
  ];

  return (
    <div className={styles.wrapper}>
      <DeviceShortInfoView device={device} />
      <div className={styles.fields}>
        {fields.map(({ label, value }, index) => (
          <div key={index} className={styles.field}>
            <span className={styles.fieldLabel}>{`${label}:`}</span>
            <span>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
